import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Image,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spinner,
  Text,
} from "@chakra-ui/react";
import checkEmail from "@root/assets/images/checkEmail.svg";
import { DeleteAccount as DeleteAccountSvg } from "@root/assets/images/deleteAccount";
import { SignOut as SignOutSvg } from "@root/assets/images/sign-out";
import { LinkText } from "@root/commonStyles/commonStyle";
import {
  CustomOutLinedButton,
  CustomButton,
} from "@root/components/common/button/customButton";
import { CustomText } from "@root/components/common/components";
import { CenterFlexGap } from "@root/components/dashboard/chat/style";
import { useTranslation } from "react-i18next";
import { CheckEmailDescription } from "./auth/checkEmail/styles";
import {
  StyledDeletePopupContainer,
  CheckYourEmailHeading,
  CheckYourEmailDesc1,
  CheckYourEmailDesc2,
} from "./style";
import { ROUTE_NAMES } from "@root/constants";
import { TFunction } from "i18next";
import { KPSLogoBig } from "@root/assets/icons";
import { ChangePasswordIcon, DeleteAccIcon } from "@root/assets";
import { Formik } from "formik";
import { PasswordInput } from "@root/components/common/passwordInput/passwordInput";
import { useState } from "react";

export const DeletePopupContent = (props: any) => {
  const { loading, onCancel, onDelete } = props;
  const { t } = useTranslation("common");
  return (
    <StyledDeletePopupContainer>
      <Image src={DeleteAccIcon} />
      <CustomText
        fontSize={"18px"}
        fontWeight={500}
        textAlign={"center"}
        text={t("delete.content")}
      />
      <CustomText
        type="grayText"
        fontSize={"16px"}
        textAlign={"center"}
        whiteSpace={"pre-line"}
        text={t("delete.description")}
      />
      <CenterFlexGap>
        <CustomOutLinedButton
          fontSize="14px"
          borderRadius="8px"
          color="black"
          border={"1px solid black"}
          height="40px"
          width="52px"
          text={t("delete.cancel")}
          onClick={onCancel}
        />
        <CustomButton
          isLoading={loading}
          text={t("delete.confirm")}
          bg="theme.danger.200"
          height="40px"
          color="white"
          borderRadius="8px"
          onClick={onDelete}
        />
      </CenterFlexGap>
    </StyledDeletePopupContainer>
  );
};

export const ChangePasswordPopupContent = (props: any) => {
  const { loading, onChangePassword } = props;
  const initialValues = {
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  };
  const { t } = useTranslation("common");
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });
  const validate = (values: any) => {
    const errors: Partial<any> = {};
    const password = values?.newPassword || "";

    // Password validation checks
    setPasswordValidation({
      length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/g?.test(password),
    });

    if (!Object.values(passwordValidation)?.every((value) => value === true))
      errors.newPassword = true;

    if (values.newPassword !== values?.confirmPassword)
      errors.confirmPassword = t("changePassword.samePassword");
    return errors;
  };

  const onSubmit = (values: any) => {
    const payload: any = {
      old_password: values?.oldPassword,
      new_password: values?.newPassword,
    };
    onChangePassword(payload);
  };
  return (
    <StyledDeletePopupContainer>
      <Image src={ChangePasswordIcon} />
      <CustomText
        fontSize={"18px"}
        fontWeight={500}
        textAlign={"center"}
        text={t("changePassword.content")}
      />
      <CustomText
        type="grayText"
        fontSize={"16px"}
        textAlign={"center"}
        whiteSpace={"pre-line"}
        text={t("changePassword.description")}
      />
      <Formik
        validate={validate}
        initialValues={initialValues}
        onSubmit={onSubmit}
      >
        {({
          handleSubmit,
          errors,
          touched,
          values,
          setFieldValue,
          handleBlur,
        }) => (
          <form onSubmit={handleSubmit}>
            <Text fontSize={"14px"} fontWeight={500}>
              {t("changePassword.oldPassword")}
            </Text>
            <FormControl
              mb={4}
              isInvalid={!!errors?.oldPassword && touched?.oldPassword}
            >
              <PasswordInput
                width={"500px"}
                // @ts-ignore
                backgroundColor={"white"}
                onBlur={handleBlur}
                onChange={(e: any) =>
                  setFieldValue("oldPassword", e?.target?.value)
                }
                borderColor={values.oldPassword ? "purple.500" : "gray.200"}
                value={values?.oldPassword}
                id="oldPassword"
                name="oldPassword"
                required
                placeholder={t("changePassword.oldPassword")}
                height={"48px"}
              />
            </FormControl>
            <Text fontSize={"14px"} fontWeight={500}>
              {t("changePassword.newPassword")}
            </Text>
            <FormControl
              mb={4}
              isInvalid={!!errors?.newPassword && touched?.newPassword}
            >
              <PasswordInput
                width={"500px"}
                // @ts-ignore
                backgroundColor={"white"}
                onBlur={handleBlur}
                onChange={(e: any) =>
                  setFieldValue("newPassword", e?.target?.value)
                }
                borderColor={values?.newPassword ? "purple.500" : "gray.200"}
                value={values?.newPassword}
                id="newPassword"
                name="newPassword"
                required
                placeholder={t("changePassword.newPassword")}
                height={"48px"}
              />

              <FormErrorMessage ml={5}>
                <ul>
                  <li
                    style={{
                      color: passwordValidation?.length ? "green" : "#E53E3E",
                    }}
                  >
                    {t("changePassword.minimumCharacters")}
                  </li>
                  <li
                    style={{
                      color: passwordValidation?.uppercase
                        ? "green"
                        : "#E53E3E",
                    }}
                  >
                    {t("changePassword.uppercaseLetter")}
                  </li>
                  <li
                    style={{
                      color: passwordValidation?.number ? "green" : "#E53E3E",
                    }}
                  >
                    {t("changePassword.atleastNumber")}
                  </li>
                  <li
                    style={{
                      color: passwordValidation?.specialChar
                        ? "green"
                        : "#E53E3E",
                    }}
                  >
                    {t("changePassword.atleastSpecialCharacter")}
                  </li>
                </ul>
              </FormErrorMessage>
            </FormControl>
            <Text fontSize={"14px"} fontWeight={500}>
              {t("changePassword.confirmPassword")}
            </Text>
            <FormControl
              mb={4}
              isInvalid={!!errors?.confirmPassword && touched?.confirmPassword}
            >
              <PasswordInput
                width={"500px"}
                // @ts-ignore
                backgroundColor={"white"}
                onBlur={handleBlur}
                onChange={(e: any) =>
                  setFieldValue("confirmPassword", e?.target?.value)
                }
                borderColor={
                  values?.confirmPassword ? "purple.500" : "gray.200"
                }
                value={values?.confirmPassword}
                id="confirmPassword"
                name="confirmPassword"
                required
                placeholder={t("changePassword.confirmPassword")}
                height={"48px"}
              />
              <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
            </FormControl>
            <Box display={"flex"} justifyContent={"center"} width={"100%"}>
              <Button
                maxWidth={"200px"}
                isLoading={loading}
                type="submit"
                colorScheme="purple"
                fontFamily="Noto Sans KR, sans-serif"
                fontWeight={500}
                width="full"
                height={"48px"}
                background="linear-gradient(180deg, #72A7DA 0%, #3075B6 100%)"
                _hover={{
                  background:
                    "linear-gradient(180deg, #72A7DA 0%, #3075B6 100%)",
                }}
              >
                {t("changePassword.content")}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </StyledDeletePopupContainer>
  );
};

export const LogoutPopupContent = (props: any) => {
  const { onCancel, onSignOut } = props;
  const { t } = useTranslation("common");
  return (
    <StyledDeletePopupContainer>
      <SignOutSvg />
      <CustomText
        pt={"8px"}
        fontSize={"24px"}
        fontWeight={600}
        fontFamily={"Poppins"}
        textAlign={"center"}
        text={t("logout.title")}
      />
      <CustomText
        type="grayText"
        fontSize={"16px"}
        fontFamily={"Poppins"}
        textAlign={"center"}
        whiteSpace={"pre-line"}
        text={t("logout.description")}
      />
      <CenterFlexGap py={"6px"}>
        <CustomOutLinedButton text={t("logout.cancel")} onClick={onCancel} />
        <CustomOutLinedButton text={t("logout.confirm")} onClick={onSignOut} />
      </CenterFlexGap>
    </StyledDeletePopupContainer>
  );
};

export const ThresholdPopupContent = (props: any) => {
  const { sliderValue = 0, handleSliderChange, onSet, onCancel } = props;
  const { t } = useTranslation("common");
  return (
    <StyledDeletePopupContainer alignItems={"flex-start"}>
      <CustomText
        fontSize={"24px"}
        fontWeight={500}
        text={t("setThreshold.content")}
      />
      <Flex justifyContent={"space-between"} width={"100%"}>
        <CustomText text="0%" fontSize={"16px"} />
        <CustomText text="100%" fontSize={"16px"} />
      </Flex>
      <Slider
        aria-label="slider-ex-1"
        defaultValue={50}
        value={sliderValue || 0}
        onChange={handleSliderChange}
      >
        <SliderTrack>
          <SliderFilledTrack />
        </SliderTrack>
        <SliderThumb boxSize={8}>
          <Flex
            justifyContent={"center"}
            alignItems={"center"}
            width={"auto"}
            px={"10px"}
            height={"34px"}
            border={"1px solid"}
            bg={"#FFF"}
            borderRadius={"100px"}
            borderColor={"gray.600"}
          >
            <CustomText
              color={"#000"}
              text={`${sliderValue || 0}%`}
              fontSize={"12px"}
              fontWeight={500}
            />
          </Flex>
        </SliderThumb>
      </Slider>
      <CustomText
        type="grayText"
        fontSize={"16px"}
        text={t("setThreshold.description")}
      />
      <CenterFlexGap py={"6px"} width={"100%"}>
        <CustomOutLinedButton
          text={t("setThreshold.cancel")}
          onClick={onCancel}
          minW={"118px"}
          justifyContent={"center"}
          alignItems={"center"}
        />
        <CustomButton
          fontSize="14px"
          height="44px"
          text={t("setThreshold.set")}
          onClick={onSet}
          minW={"118px"}
          justifyContent={"center"}
          alignItems={"center"}
        />
      </CenterFlexGap>
    </StyledDeletePopupContainer>
  );
};

const RenderContent = (
  loading: boolean,
  onResend: () => void,
  t: TFunction
) => {
  if (loading) {
    return (
      <Flex my={"20px"}>
        <Spinner />
      </Flex>
    );
  } else {
    return (
      <Flex alignItems={"center"} mt={"18px"}>
        <CustomButton
          py={"10px"}
          colorScheme="purple"
          justifyContent={"center"}
          size="md"
          alignItems={"center"}
          text={t("translate.resendBtnText")}
          onClick={onResend}
          background={"purple.purpleGradient"}
          width={"105px"}
          borderRadius={"16px"}
        />
      </Flex>
    );
  }
};

// Check your Email
export const VerifyYourEmailAfterSignup = ({
  email,
  onResend,
  loading,
}: any) => {
  const { t } = useTranslation("common");
  return (
    <Flex flexDir={"column"} alignItems={"center"}>
      <Box mt={"5rem"} mb={"15px"}>
        <KPSLogoBig width={"254px"} height={"48px"} />
      </Box>
      <Image src={checkEmail} alt="forgetpassword" mt="26px" />
      <CheckYourEmailHeading>
        {t("translate.pleaseVerify")}
      </CheckYourEmailHeading>

      <CheckYourEmailDesc1>
        {t("translate.verifyDesc1", { email })}
      </CheckYourEmailDesc1>
      <CheckYourEmailDesc2>{t("translate.verifyDesc2")}</CheckYourEmailDesc2>
      {RenderContent(loading, onResend, t)}
      <LinkText
        my="38px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap="8px"
        to={ROUTE_NAMES.FORGET_PASSWORD}
      >
        {t("checkEmail.differentEmail")}
      </LinkText>
    </Flex>
  );
};

export const VerifyYourEmailAfterReset = ({
  email,
  loading,
  onResend,
}: any) => {
  const { t } = useTranslation("common");
  return (
    <Flex
      flexDir={"column"}
      alignItems={"center"}
      gap={"16px"}
      maxW={{ base: "90%", sm: "600px" }}
    >
      <Box mt={"5rem"} mb={"15px"}>
        <KPSLogoBig width={"254px"} height={"48px"} />
      </Box>

      <Image src={checkEmail} alt="Lock" />
      <CustomText
        textAlign={"center"}
        fontSize={"32px"}
        text={t("translate.pleaseVerify")}
        fontWeight={500}
      />
      <CheckEmailDescription>
        {t("checkEmail.thanksIf")}

        <LinkText
          fontSize={{ base: "12px", md: "16px" }}
          fontWeight={"400"}
          to={""}
          mx={"6px"}
        >
          {email || ""}
        </LinkText>

        {t("checkEmail.description")}
      </CheckEmailDescription>
      <CustomText
        textAlign={"center"}
        type="grayText"
        fontSize={"16px"}
        text={t("translate.checkAfter5Mins")}
      />
      <Button
        isLoading={loading}
        onClick={onResend}
        mt="32px"
        size={"md"}
        colorScheme="purple"
        width="104px"
        background="linear-gradient(180deg, #72A7DA 0%, #3075B6 100%)"
        _hover={{
          background: "linear-gradient(180deg, #72A7DA 0%, #3075B6 100%)",
        }}
      >
        {t("checkEmail.resend")}
      </Button>
      <LinkText my="38px" to={"/forgot-password"}>
        {t("checkEmail.differentEmail")}
      </LinkText>
    </Flex>
  );
};
